/**
 * MONSTER TOWN — THE TOWN SHOP. OWNED BY THE VENUE/ECONOMY CREW (src/economy/**).
 *
 * A shop a kid WALKS INTO, not a menu. Every purchasable thing sits on its own
 * plinth as a real 3D model of itself, with its price spelled out underneath in
 * coin discs — big coin = 100, middle = 10, little = 1. That is deliberate:
 * "prices and coin counting are the real math, and it works because they want
 * the thing" (brief). No numerals are required to read a price, and no reading
 * is required at all (LAW #4).
 *
 * The shelves restock themselves from `wallet.shopCatalogue()`, so anything ANY
 * crew registers — furniture, decor, building pieces, outfits — appears here
 * the moment its unlock tier lands. Adding stock is a content drop.
 *
 * NOTHING IS EVER TAKEN AWAY: buying is the only way coins leave the wallet, it
 * can never go negative, and "not enough yet" is a friendly wobble, never a
 * scold or a locked door.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { priceOf, wallet } from '../core/economy'
import { save } from '../core/save'
import { world } from '../core/scene'
import type { Cell } from '../core/grid'
import type { InstanceId, MonsterConfig, PlaceableId, SceneId } from '../core/types'
import { place, removeInstance } from '../build/placer'
import { instanceCentre, stateOf } from '../app/interactions'
import { buildMonster } from '../monster/art'
import { mix as mixPalette } from '../core/palette'
import { boxOn } from '../render/kit'
import { registerRoom, type RoomSpec } from '../venue/scene'
import { shopSignArt, shopWallUnitArt } from '../venue/shopArt'
import { TOWN_SHOP_SCENE, shopPlinth } from '../content/venue'

/** Cheapest first, so the shelf nearest the door is the one you can afford. */
const MAX_SLOTS = 15

/**
 * Origin puts cell (8,5) — the way out — under the world crew's town shop
 * front door at world (34, 0), so walking in lands you in the doorway.
 */
export const TOWN_SHOP_ROOM: RoomSpec = {
  sceneId: TOWN_SHOP_SCENE,
  devName: 'Town shop',
  grid: { id: 'shop', scale: 'interior', width: 12, depth: 10, levels: 4, originX: 25.5, originZ: -5 },
  shell: {
    wall: 'wallLilac',
    // GOLD CORNICE, NOT PURPLE (venue fix, r3). `roofPurple` on `wallLilac` is
    // one hue, so the band along the top of the wall had no edge against the
    // wall below it OR against the dusk-coloured surround above it, and the
    // round-3 critic read the result as "a purple band of the backdrop void
    // showing through a gap between the wall and roof". There is no gap; there
    // was no contrast. Gold reads as trim from anywhere in the room, and it is
    // the shop's own colour — the same coin the sign and the purse wear.
    wallTrim: 'roofMustard',
    floor: 'floorTile',
    floorAlt: 'wallLilac',
    roofBeam: 'woodDark',
    // A harlequin diamond, not the venues' chequer. The shop, the parlour and
    // the restaurant were three colourways of one floor; now they are three
    // floors. And no centre rug — the middle of this room is the AISLE.
    floorStyle: 'diamond',
    rug: null,
  },
}

export const SHOP_DOOR_CELL: Cell = { gx: 8, gy: 0, gz: 5 }

/** The counter, against the west wall, with the shopkeeper behind it. */
const COUNTER_CELL: Cell = { gx: 1, gy: 0, gz: 3 }
export const SHOP_COUNTER_CELL: Cell = COUNTER_CELL

/**
 * WHERE THE SHELVES STAND — TWO WALL RUNS AND A CLEAR AISLE.
 *
 * The old layout scattered plinths across four rows spanning the whole floor,
 * which produced two problems in the same frame. It looked like a grid of
 * identical boxes rather than a shop; and it put a shelf directly next to
 * wherever the kid lands on walking in, so the player monster and the item on
 * the shelf occupied the same patch of screen and read as clipping through each
 * other.
 *
 * Shelves now line the back (gz 1) and the front (gz 8) walls, and the middle
 * band gz 3..6 is kept EMPTY. That is the aisle you walk down, it is where the
 * door mat is, and standing in it there is nothing within a tile of you.
 *
 * NOTHING MAY SIT IN THE DOOR'S SHADOW. The way out is a mat in the middle of a
 * 12x10 floor and a shelf on one of its eight neighbours is unreachable under a
 * fixed iso camera: the raycast hits the doorframe and `pickInstance`'s
 * (deliberately generous) 44px fallback resolves to the door as well. An item
 * permanently for sale that a kid can neither tap nor see. The door's whole
 * neighbourhood is filtered out, so this stays correct if the door ever moves.
 */
function slots(): Cell[] {
  const blocked = new Set<string>()
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      blocked.add(`${SHOP_DOOR_CELL.gx + dx},${SHOP_DOOR_CELL.gz + dz}`)
    }
  }
  // the counter is 2x1, so it owns two cells
  blocked.add(`${COUNTER_CELL.gx},${COUNTER_CELL.gz}`)
  blocked.add(`${COUNTER_CELL.gx + 1},${COUNTER_CELL.gz}`)

  // Interleaved back/front, so a shop that is not full yet fills BOTH runs
  // evenly rather than leaving one wall bare and the other packed.
  const out: Cell[] = []
  for (const gx of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]) {
    for (const gz of [1, 8]) {
      if (blocked.has(`${gx},${gz}`)) continue
      out.push({ gx, gy: 0, gz })
    }
  }
  return out
}

/** Standing room: the aisle cell a kid is put down in on walking through. */
export const SHOP_AISLE_CELL: Cell = { gx: 8, gy: 0, gz: 4 }

export interface ShopEntry {
  defId: PlaceableId
  cost: number
  owned: boolean
  affordable: boolean
}

/**
 * THE SHOP MODEL. Also the data the HUD crew's shop panel should render if it
 * wants a flat list — this is the single source of truth for what is for sale.
 */
export function shopModel(): ShopEntry[] {
  return wallet
    .shopCatalogue()
    .map((e) => ({ defId: e.id, cost: e.cost, owned: e.owned, affordable: e.affordable }))
    .sort((a, b) => (a.owned === b.owned ? a.cost - b.cost : a.owned ? 1 : -1))
}

// ---------------------------------------------------------------------------
// stocking the shelves
// ---------------------------------------------------------------------------

let restocking = false

export function restockShop(): void {
  const node = world.get(TOWN_SHOP_SCENE)
  if (node === undefined || restocking) return
  restocking = true
  try {
    for (const p of node.grid.all()) {
      if (p.defId === shopPlinth.id) removeInstance(TOWN_SHOP_SCENE, p.instanceId, true)
    }
    const entries = shopModel().slice(0, MAX_SLOTS)
    const cells = slots()
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i]
      const anchor = cells[i]
      if (e === undefined || anchor === undefined) continue
      place(TOWN_SHOP_SCENE, shopPlinth.id, anchor, 0, {
        silent: true,
        state: { offer: e.defId, cost: e.cost, owned: e.owned },
      })
    }
  } finally {
    restocking = false
  }
}

// ---------------------------------------------------------------------------
// buying
// ---------------------------------------------------------------------------

/**
 * A TAP ON A SHELF IS A QUESTION, NOT A PURCHASE.
 *
 * This used to call `wallet.buy` directly: one tap on a plinth and the coins
 * were gone, with no panel, no confirm and nothing on screen that ever said what
 * had been bought. In a room of fifteen near-identical white plinths that meant
 * a three-year-old could empty the purse by tapping the shiny things, and a
 * purchase — unlike a placement — has no undo. It was the only place in the game
 * where a mis-tap cost something a kid could not get back.
 *
 * So the tap now OFFERS. The HUD's card shows the thing big, its price in
 * countable coins, and a yes and a no the same size as each other; the coins
 * move only when `shop:confirm` comes back. Nothing here decides anything on its
 * own any more.
 */
function offerFromPlinth(sceneId: SceneId, instanceId: InstanceId): void {
  const st = stateOf(sceneId, instanceId)
  const offer = typeof st?.['offer'] === 'string' ? (st['offer'] as PlaceableId) : null
  if (offer === null) return

  const owned = wallet.owns(offer)
  if (owned) {
    // Already yours, and yours forever — you bought the catalogue entry, not
    // the object, so it is infinitely placeable. Say so with a happy sparkle.
    bus.emit('audio:cue', { cue: 'chime_happy' })
    bus.emit('ui:toast', { icon: 'tick' })
    return
  }

  const cost = priceOf(offer)
  const have = wallet.coins
  const affordable = wallet.canAfford(offer)
  bus.emit('shop:offer', {
    defId: offer,
    sceneId,
    instanceId,
    cost,
    have,
    owned,
    affordable,
  })
  bus.emit('audio:cue', { cue: 'ui_open' })
  if (!affordable) {
    // Friendly "not yet": the price coins jiggle so a kid sees HOW MANY more.
    // The card still opens, so they at least get a good look at the thing.
    bus.emit('shop:tooDear', { defId: offer, need: Math.max(0, cost - have), have })
    jiggle(sceneId, instanceId)
  }
}

/** The kid said yes. THE ONLY PLACE COINS LEAVE THE PURSE IN THIS ROOM. */
function confirmBuy(defId: PlaceableId, sceneId: SceneId, instanceId: InstanceId): void {
  const result = wallet.buy(defId)
  if (!result.ok) {
    bus.emit('shop:tooDear', { defId, need: result.need, have: result.have })
    bus.emit('audio:cue', { cue: 'nope_soft' })
    bus.emit('ui:toast', { icon: 'coin' })
    jiggle(sceneId, instanceId)
    return
  }
  bus.emit('shop:bought', { defId, cost: result.spent, balance: result.balance })
  bus.emit('audio:cue', { cue: 'coin_spend' })
  // SAY WHAT JUST HAPPENED, TWICE, IN PICTURES. A big picture of the thing flies
  // up off the shelf it came from, and a bag lands in the toast rail. Before
  // this, the only evidence a purchase had occurred was the purse quietly
  // counting down in the corner.
  const at = instanceCentre(sceneId, instanceId)
  if (at !== null) bus.emit('ui:fly', { icon: 'bag', at, amount: result.spent })
  bus.emit('ui:toast', { icon: 'bag', ms: 2200 })
  restockShop()
  save.request('shop:bought')
}

const jiggles = new Map<string, number>()

function jiggle(sceneId: SceneId, instanceId: InstanceId): void {
  jiggles.set(`${sceneId}#${instanceId}`, 0)
}

/** Runs from the economy system's render pass. Cheap: only active jiggles. */
export function updateJiggles(dt: number): void {
  if (jiggles.size === 0) return
  for (const [key, t] of [...jiggles]) {
    const next = t + dt
    const hash = key.indexOf('#')
    const sceneId = key.slice(0, hash) as SceneId
    const id = key.slice(hash + 1) as InstanceId
    const art = world.get(sceneId)?.art.get(id)
    const price = art?.getObjectByName('price') as THREE.Object3D | undefined
    if (price === undefined || next > 0.7) {
      if (price !== undefined) price.position.x = price.userData['baseX'] ?? price.position.x
      jiggles.delete(key)
      continue
    }
    if (price.userData['baseX'] === undefined) price.userData['baseX'] = price.position.x
    const baseX = price.userData['baseX'] as number
    price.position.x = baseX + Math.sin(next * 40) * 0.06 * (1 - next / 0.7)
    jiggles.set(key, next)
  }
}

// ---------------------------------------------------------------------------
// THE ROOM ITSELF — a shopkeeper, a sign, and shelving on the walls
// ---------------------------------------------------------------------------

/**
 * SOMEBODY IS SERVING.
 *
 * The captured frame of this room had no counter, no signage and nobody in it —
 * a kid walked into a warehouse of pedestals. A shopkeeper is one monster and
 * costs one rig; it is the difference between "a room with stock in it" and "a
 * shop", and it is the same creature the customers and the player are built
 * from, so it cannot drift into a different art style.
 *
 * She lives in `statics`, not `actors`: she never moves off her spot, she is
 * never saved, and building her once means the room's draw calls do not grow.
 * Reaching into the monster crew's `buildMonster` is the same sanctioned
 * exception `customers.ts` documents — it is a pure config-in/group-out factory,
 * and ONE character system is the whole point.
 */
const SHOPKEEPER: MonsterConfig = {
  shape: 'round',
  color: 'monsterBubblegum',
  accentColor: 'monsterCream',
  eyes: 'two',
  horns: 'curved',
  pattern: 'belly',
  hat: null,
  outfit: null,
}

let dressed = false

function dressShopRoom(): void {
  if (dressed) return
  const node = world.get(TOWN_SHOP_SCENE)
  if (node === undefined) return
  dressed = true
  const g = node.grid
  const t = g.tileSize

  /*
   * The shopkeeper, standing BEHIND the counter and facing the room. A rig
   * faces +Z as built, and the room is +Z of the counter, so no rotation — she
   * was turned a quarter turn and stood half inside the till.
   *
   * ...ON A STEP, AND A WHOLE STEP BACK (venue fix, r3).
   *
   * `round` is a one-unit-tall body and the counter top is 0.87 of a unit, so
   * standing her on the floor put five-sixths of her behind it: the round-3
   * critic read the result as clipping — "the pink shopkeeper clips through the
   * counter, its lower body is inside the desk" — and that is what it looks
   * like whether or not the volumes actually intersect. Shopkeepers stand on a
   * duckboard for exactly this reason. On the step her belly clears the top, so
   * she is a person serving rather than a head on a desk, and the extra clear
   * tile behind means nothing of her is inside the furniture at any camera snap.
   */
  const keeper = buildMonster(SHOPKEEPER, { detail: 'simple' })
  const at = g.cellToWorld({ gx: COUNTER_CELL.gx + 1, gy: 0, gz: COUNTER_CELL.gz - 1 })
  const kx = at.x - t * 0.55
  const kz = at.z - t * 0.62
  const step = boxOn(t * 1.5, t * 0.34, t * 0.8, {
    color: mixPalette('woodDark', 'wood', 0.5),
    x: kx,
    y: 0,
    z: kz,
    name: 'keeperStep',
  })
  step.castShadow = false
  node.statics.add(step)
  keeper.root.position.set(kx, t * 0.34, kz)
  keeper.root.name = 'shopKeeper'
  node.statics.add(keeper.root)

  // The hanging coin sign goes over the AISLE, not over the shelves. Hung on
  // the back wall it sat in front of the wall units and read as an orange bar
  // floating across the stock. Over the middle of the empty floor it is the
  // first thing you see on walking in, it is above head height so it can never
  // cover the thing a kid is tapping, and there is nothing behind it.
  // THE SIGN HANGS OVER THE ENTRANCE, in the clear.
  //
  // Hung over the shelves it sat on top of the stock; hung over the counter it
  // projected up out of the framed room entirely. Just inside the door it is
  // the first thing you see on walking in, there is nothing but floor behind
  // it, and it is well above head height so it can never cover a tap target.
  // It casts no shadow — a sign-shaped shadow on the aisle looked like an
  // object lying on the floor.
  const sign = shopSignArt(t)
  const door = g.cellToWorld(SHOP_DOOR_CELL)
  sign.position.set(door.x, g.levelHeight * 1.2, door.z + t * 1.2)
  sign.traverse((o) => {
    o.castShadow = false
    o.receiveShadow = false
  })
  node.statics.add(sign)

  // Shelving on the back wall, either side of the sign. Part of the shell, so
  // it is one build with no picking and no state.
  for (let i = 0; i < 2; i++) {
    const unit = shopWallUnitArt(t, i * 5 + 1)
    unit.position.set(g.originX + t * (i === 0 ? 2.2 : 9.4), 0, g.originZ + t * 0.42)
    node.statics.add(unit)
  }
}

export function installShop(): void {
  registerRoom(TOWN_SHOP_ROOM)
  dressShopRoom()

  bus.on('play:interacted', (e) => {
    if (e.defId !== shopPlinth.id) return
    offerFromPlinth(e.sceneId, e.instanceId)
  })

  bus.on('shop:confirm', (e) => confirmBuy(e.defId, e.sceneId, e.instanceId))

  bus.on('shop:cancel', () => {
    // Backing out costs nothing and is never treated as a mistake.
    bus.emit('audio:cue', { cue: 'ui_close' })
  })

  bus.on('scene:entered', (e) => {
    if (e.scene === TOWN_SHOP_SCENE) restockShop()
  })

  // A new tier means new stock: the shop is different next time you walk in.
  bus.on('economy:unlocked', () => restockShop())
}

/** Harness/debug: buy the cheapest thing on the shelf, the way a kid would. */
export function debugBuyCheapest(): { ok: boolean; defId: PlaceableId | null; cost: number } {
  const entry = shopModel().find((e) => !e.owned)
  if (entry === undefined) return { ok: false, defId: null, cost: 0 }
  const r = wallet.buy(entry.defId)
  if (r.ok) {
    bus.emit('shop:bought', { defId: entry.defId, cost: r.spent, balance: r.balance })
    restockShop()
  }
  return { ok: r.ok, defId: entry.defId, cost: priceOf(entry.defId) }
}
